import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ehAdmin, papeisDe } from '../../shared/papeis';
import { getPendingApprovalsCount } from '../../api/notificationsApi';
import { logoutUser } from '../../api/authApi';
import { getPendingSLACount, getMyRejections } from '../../api/workSessionsApi';
import { getPendingFormsCount } from '../../api/formsApi';
import { getAutorizacoesPendentesCount, ORCAMENTO_CILIA_HABILITADO } from '../../api/orcamentoCiliaApi';
import {
  PORTAL_COTACOES_HABILITADO,
  contarAvisosNaoLidosPortal,
} from '../../api/portalCotacoes/portalCotacoesApi';
import { BANCO_SYNC_HABILITADO } from '../../api/bancoSync/bancoSyncApi';
import { FIPE_SYNC_HABILITADO } from '../../api/fipeSync/fipeSyncApi';
import { CSSTransition } from 'react-transition-group';
import { createPortal } from 'react-dom';
import { useDispatch } from 'react-redux';
import { clearAccessToken } from '../../views/auth/reducer';
import { useNavigate, useLocation } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';
import { getUserById, User } from '../../api/usersApi';
import { notify } from '../../shared/feedback/notify';
import { salvarTemaDaAplicacao } from '../../api/aparenciaApi';
import { definirTemaConfigurado, TemaConfigurado, useTemaConfigurado } from '../../shared/tema/tema';
import { useNotificacoesPorTela } from '../sino-notificacoes/useNotificacoesPorTela';

import Logo from './img/atual-logo.svg';
import LogoFechado from './img/logo-fechado.svg';
import ButtonAbrir from './img/button-abrir.svg';
import ButtonFechar from './img/button-fechar.svg';
import PainelGeral from './img/painel-geral.svg';
import Diagrama from './img/diagram.svg';
import AdminSistemaIcon from './img/admin-sistema.svg';
import ChecklistHistorico from './img/checklist-historico.svg';
import ChefeDeOficina from './img/chefe-de-oficina.svg';
import { lerTokenDoCookie, limparCookieDaSessao } from '../../shared/cookie-da-sessao';
// previsao-pecas/at-01: o ícone de peças que já existia no repositório, sem uso desde a suspensão da V2.
import PrevisaoPecasIcon from './img/movimentacao-de-pecas.svg';
// [FUTURE-REFATORACAO: REMOVE_ESTOQUE_ORCAMENTO]
// Ícone do item de menu de Logs de Peças, comentado abaixo. Volta junto com ele.
// import MovimentacaoDePecas from './img/movimentacao-de-pecas.svg';
import Recepcao from './img/recepcao.svg';
import TelaDeControle from './img/tela-de-controle.svg';
import RastreamentoIcon from './img/rastreamento.svg';
// Ícone PRÓPRIO (at-85): etapas + relógio. Reusar o `clock.svg` do "Tempo e SLA" deixaria dois
// itens com o mesmo desenho para ADMIN e TI, que veem as duas telas — o defeito que a at-83
// corrigiu no Portal de Cotações.
import AndamentoIcon from './img/andamento.svg';
import MinhasVisitasIcon from './img/minhas-visitas.svg';
import VeiculosOficinaExternaIcon from './img/veiculos-oficina-externa.svg';
import FipeIcon from './img/fipe.svg';
import AdministracaoIcon from './img/administracao.svg';
// Ícone PRÓPRIO desde 14/08/2026: até então o Portal reusava o `orcamento.svg` e ficava
// IDÊNTICO ao item "Orçamentos Cilia" logo acima — dois módulos distintos, o mesmo desenho.
import PortalCotacoesIcon from './img/cotacoes.svg';
// O import do Cilia se chamava `Orcamento` e sumiu na limpeza da at-72 (que removeu o item
// comentado de Orçamento junto com o ícone dele) — daí o nome novo.
import OrcamentoIcon from './img/orcamento.svg';
import Relatorio from './img/relatorio.svg';
import ContacCategoria from './img/contac-categoria.svg';
// at-86: ícones próprios das duas telas de painel. O medidor fala de tempo e desempenho da
// operação; o escudo com barras fala da carteira protegida vista em números.
import DashboardOficinaIcon from './img/dashboard-oficina.svg';
import DashboardAssociacaoIcon from './img/dashboard-associacao.svg';
// at-100: ícone PRÓPRIO do Dashboard de Compras. Até 11/09/2026 o item reusava o
// `DashboardOficinaIcon` e ficava idêntico ao "Dashboard Oficina" no menu — a mesma colisão que
// fez o Portal de Cotações ganhar ícone próprio na at-07 (ver `cotacoes.svg`).
import DashboardComprasIcon from './img/dashboard-compras.svg';
import ContacPlaca from './img/contac-placa.svg';
import ContacConsultor from './img/contac-consultor.svg';
import Perfil from './img/perfil.svg';
import MeusCarros from './img/meus-carros.svg';
import Logout from './img/logout.svg';
import ClockIcon from '../../img/clock.svg';
import HandshakeIcon from '../../img/handshake.svg';
import ChartPie from '../../img/chart-pie.svg';
import { ModuloNovo, moduloEmModoTeste, modulosNovosSomenteAdmin } from '../../shared/modulos-novos';

import './Sidebar.css';

interface SidebarProps {
  className?: string;
  isSidebarOpen: boolean;
  toggleSidebar: () => void;
}

interface DecodedToken {
  sub: string;
  userType:
    | 'RECEPCAO'
    | 'CHEFE_DE_OFICINA'
    | 'ORCAMENTO'
    | 'ESTOQUE'
    | 'COMPRA'
    | 'COLABORADOR'
    | 'CONSULTOR'
    | 'REGIONAL'
    | 'RELACIONAMENTO'
    | 'CONTAC'
    | 'COMISSIONAMENTO'
    | 'ADMINISTRACAO'
    | 'ADMIN'
    | 'TI'
    | 'RASTREADOR'
    | 'EVENTOS'
    | 'INSPETOR'
    | 'TELAO'
    | 'GESTAO_COMPRAS';
  isAdmin: boolean;
}

interface SidebarItem {
  path: string;
  label: string;
  icon: string;
  badge?: number;
  action?: () => void;
}

/** Alias de leitura para indexar por papel vindo do token (string). A tabela segue tipada. */
const permissionsPorPapel = (): Record<string, string[]> => permissions as Record<string, string[]>;

const adminPermissions = [
  '/home',
  '/checklists-realizados',
  '/formularios',
  '/comissionamentos',
  '/recepcao',
  '/chefe-de-oficina',
  '/controle-producao',
  '/parts/logs',
  '/auditoria',
  '/tela-de-controle',
  '/administracao',
  '/portal-cotacoes',
  // previsao-pecas/at-01: o ADMIN é o único que a vê em produção enquanto o módulo estiver em teste.
  // (A tela de Estoque, `/estoque`, foi absorvida por ela na previsao-pecas/at-02.)
  '/previsao-pecas',
  '/contac-dashboard',
  '/contac-dashboard-regional',
  '/contac-dashboard-consultor',
  // [FUTURE-REFATORACAO: REMOVE_COOPERATIVAS]
  // '/contac-dashboard-cooperativa',
  '/resultado-por-placa',
  '/users-table',
  '/admin/sistema',
  '/orcamentos-cilia',
  '/rastreamento',
  '/andamento-veiculos',
  // at-114: as duas faces da Oficina Externa — a do inspetor (com a fila offline) e a leitura.
  '/oficina-externa',
  '/oficina-externa/fila',
  '/oficinas-externas',
  // at-86: Dashboard da Associação. Fora daqui, só o CONTAC — nem o TI (que não tem Contac,
  // ADR 0012), nem RELACIONAMENTO/CONSULTOR/REGIONAL, que têm isolamento de dados ou não veem
  // valor: o painel reúne valor protegido da carteira e arrecadação, sem filtro por identidade.
  '/contac/dashboard-associacao',
  // contac/at-11: Radar Financeiro. Mesmo recorte do Dashboard da Associação, pelos mesmos
  // motivos: o TI não tem Contac (ADR 0012), o RELACIONAMENTO nunca vê valor, e
  // CONSULTOR/REGIONAL têm isolamento por identidade que estes detectores não aplicam.
  '/contac/radar',
  // at-86: Dashboard Oficina. Aparece SÓ aqui, nem para o TI: a tela cruza produtividade por
  // pessoa (horas por colaborador, percentual de pausa), que não é a leitura de suporte do
  // ADR 0012.
  '/dashboard-oficina',
  // at-100: Dashboard de Compras. O ADMIN vê aqui; a GESTAO_COMPRAS recebe o item pela lista do
  // papel dela (abaixo) — o COMPRA inteiro deixou de ver na at-125. O TI fica de fora pelo mesmo motivo do Dashboard Oficina — a tela cruza carga
  // por comprador, que é produtividade de pessoa nomeada.
  '/dashboard-compras',
  '/sincronizacao-base',
  '/sincronizacao-fipe',
  '/logout',
];

const permissions: Record<DecodedToken['userType'], string[]> = {
  RECEPCAO: ['/home', '/checklists-realizados', '/formularios', '/recepcao', '/logout'],

  CHEFE_DE_OFICINA: [
    '/home',
    '/checklists-realizados',
    '/formularios',
    '/chefe-de-oficina',
    '/controle-producao',
    '/auditoria',
    // at-107: o Chefe passou a gerar o Relatório de Veículos, e o botão vive nesta tela
    // (decisão do Victor, 17/09/2026). Sem o item no menu, a tela ficaria inalcançável para
    // ele — permissão que ninguém usa.
    '/tela-de-controle',
    // at-114: o Chefe acompanha os veículos que estão em oficina externa — a leitura é dele e do
    // setor de Eventos (decisão do Victor, 24/09/2026).
    '/oficinas-externas',
    // Orçamentos Cilia: o Chefe entra pela aba "Autorizações" (30/07/2026)
    '/orcamentos-cilia',
    // previsao-pecas/at-01: o Chefe acompanha quando chega a peça que o veículo espera.
    '/previsao-pecas',
    '/logout',
  ],

  ORCAMENTO: [
    '/home',
    '/formularios',
    '/orcamentos-cilia',
    '/logout',
  ],

  ESTOQUE: [
    '/home',
    '/formularios',
    '/auditoria',
    // previsao-pecas/at-02: a tela do ofício do ESTOQUE — onde a chegada de cada peça é
    // conferida. Absorveu a antiga tela de Estoque (`/estoque`, at-86).
    '/previsao-pecas',
    '/logout',
  ],

  COMPRA: [
    '/home',
    '/formularios',
    '/auditoria',
    '/portal-cotacoes',
    /*
     * ⚠️ **Sem `/estoque`** — decisão do Victor, 21/09/2026, revertendo o que a at-86 havia
     * decidido no mesmo dia. A tela é da área de Estoque, e registrar a chegada física não é
     * trabalho de quem compra: dar as duas pontas ao mesmo papel apagaria a separação que a tela
     * existe para sustentar. A consequência aceita é a de sempre — enquanto não houver usuário
     * ESTOQUE em produção, só o ADMIN alcança a fila de chegada.
     */
    // at-125 (reaplicada em 28/10/2026): SEM `/dashboard-compras`. O painel é do papel
    // GESTAO_COMPRAS: nem todo usuário de Compras pode ver os números dele (decisão do Victor).
    // orcamento-cilia/at-15: Compras decide a compra a partir da cotação online, que vive na
    // tela de Requisições — em LEITURA (a conferência continua sendo do orçamentista).
    '/orcamentos-cilia',
    // previsao-pecas/at-01: Compras é quem confirma o prazo com o fornecedor.
    '/previsao-pecas',
    '/logout',
  ],

  COLABORADOR: [
    '/home',
    '/formularios',
    '/controle-producao',
    '/logout',
  ],

  RELACIONAMENTO: ['/resultado-por-placa', '/logout'],

  CONSULTOR: [
    '/formularios',
    '/contac-dashboard',
    '/contac-dashboard-consultor',
    '/resultado-por-placa',
    '/logout',
  ],

  REGIONAL: [
    '/formularios',
    '/contac-dashboard',
    '/contac-dashboard-regional',
    '/contac-dashboard-consultor',
    '/resultado-por-placa',
    '/logout',
  ],

  CONTAC: [
    '/contac-dashboard',
    '/contac-dashboard-regional',
    '/contac-dashboard-consultor',
    // [FUTURE-REFATORACAO: REMOVE_COOPERATIVAS]
    // '/contac-dashboard-cooperativa',
    '/resultado-por-placa',
    // at-86: o painel da carteira. Não vale para CONSULTOR/REGIONAL (isolamento por identidade)
    // nem para RELACIONAMENTO (nunca vê valor).
    '/contac/dashboard-associacao',
    // contac/at-11: as recomendações proativas sobre a mesma base.
    '/contac/radar',
    // 🔴 EMBARGO da contac/at-18: `/admin/sistema` saiu daqui junto com o cartão do Contac.
    // A config do módulo (contac/at-13) era a ÚNICA que o papel alcançava, e o hub redirecionava
    // direto para ela; sem o cartão, o item de menu levaria à faixa "Nenhum módulo de
    // configuração está disponível para o seu perfil... acione a TI" — mandando o usuário abrir
    // chamado por um embargo deliberado. As duas linhas voltam juntas quando ele cair.
    '/logout',
  ],

  COMISSIONAMENTO: [
    '/comissionamentos',
    '/formularios',
    // comissionamentos/at-01: o hub redireciona quem alcança um módulo só, então este papel cai
    // direto na config de Comissionamentos — a única que ele enxerga. É por aqui que se chega ao
    // disparo manual da sincronização de boletos.
    '/admin/sistema',
    '/logout',
  ],

  ADMINISTRACAO: [
    '/home',
    '/administracao',
    '/auditoria',
    '/logout',
  ],

  ADMIN: adminPermissions,

  // TI: todas as telas da Oficina (sem Contac e sem Comissionamentos)
  TI: [
    '/home',
    '/checklists-realizados',
    '/formularios',
    '/recepcao',
    '/chefe-de-oficina',
    '/controle-producao',
    '/parts/logs',
    '/auditoria',
    '/tela-de-controle',
    '/administracao',
    '/portal-cotacoes',
    '/users-table',
    '/admin/sistema',
    '/orcamentos-cilia',
    '/rastreamento',
    '/andamento-veiculos',
    // at-114: o TI enxerga o acompanhamento de oficina externa em leitura (ADR 0012). As telas
    // do INSPETOR ficam de fora: ali ele agiria — cadastrar veículo, registrar visita.
    '/oficinas-externas',
    '/sincronizacao-base',
    '/sincronizacao-fipe',
    // previsao-pecas/at-02: o TI vê em leitura (decisão do Victor, 24/10/2026).
    '/previsao-pecas',
    '/logout',
  ],

  // at-75 (ADR 0033): UMA tela e sair. Papel de leitura estreita, como o RELACIONAMENTO.
  RASTREADOR: ['/rastreamento', '/logout'],

  /*
   * at-85 (ADR 0039): UMA tela e sair, mesmo desenho do RASTREADOR.
   *
   * ⚠️ at-90: ganhou a **Auditoria**, por causa da aba Feedback (decisão do Victor, 03/09/2026)
   * — emenda consciente ao ADR 0039. O que ele vê lá dentro é a matriz de abas que decide, e a
   * aba Feedback nasce sem papel nenhum: até o ADMIN conceder, o item existe no menu e a tela
   * diz "nenhuma área liberada para o seu perfil". Consequência aceita, registrada no ADR 0042.
   */
  EVENTOS: ['/andamento-veiculos', '/oficinas-externas', '/auditoria', '/logout'],

  /*
   * at-114 (ADR 0059): o INSPETOR vê SÓ a Oficina Externa.
   *
   * É a separação de ambientes que o Victor pediu em 24/09/2026, e ela se faz por PAPEL, não por
   * deploy: nada da oficina interna entra aqui — nem a Home. O processo lá fora é outro (não há
   * esteira, não há sessão de trabalho, há visita), e misturar as duas telas faria o inspetor
   * navegar por um fluxo que não é o dele.
   */
  INSPETOR: ['/oficina-externa', '/oficina-externa/fila', '/logout'],

  // at-137: a TV da oficina. Só a Home (o Telão é aba dela e abre em tela cheia em `/telao`).
  TELAO: ['/home', '/logout'],
  /*
   * at-125: a gestão de Compras — o único papel, além do ADMIN, que vê o Dashboard de Compras.
   * Ele vem SOMADO ao COMPRA, que continua dando o resto do módulo.
   */
  GESTAO_COMPRAS: ['/dashboard-compras', '/logout'],
};

const userTypeLabels: Record<string, string> = {
  RECEPCAO: 'Recepção',
  CHEFE_DE_OFICINA: 'Chefe de Oficina',
  ORCAMENTO: 'Orçamento',
  ESTOQUE: 'Estoque',
  COMPRA: 'Compra',
  COLABORADOR: 'Colaborador',
  CONSULTOR: 'Consultor',
  REGIONAL: 'Regional',
  RELACIONAMENTO: 'Relacionamento',
  CONTAC: 'Contac',
  COMISSIONAMENTO: 'Comissionamento',
  ADMINISTRACAO: 'Administração',
  ADMIN: 'Administrador',
  TI: 'TI',
  RASTREADOR: 'Rastreador',
  EVENTOS: 'Eventos',
  INSPETOR: 'Inspetor',
  TELAO: 'Telão',
  GESTAO_COMPRAS: 'Gestão de Compras',
};

const getInitials = (name?: string) => {
  if (!name || name === '...') return '?';
  const cleanName = name.trim();
  if (!cleanName) return '?';
  const parts = cleanName.split(/\s+/).slice(0, 2);
  return parts.map((part) => part.charAt(0)).join('').toUpperCase();
};

/**
 * Os contadores do menu, recarregados a cada 30s (BUG-005, at-95).
 *
 * Cada um tem estado próprio: o aviso sai só na TRANSIÇÃO de funcionando para falhando (antes
 * reaparecia a cada ciclo, indefinidamente) e diz QUAL contador falhou — dois deles são de módulos
 * satélites, e um módulo fora do ar não pode parecer o menu inteiro quebrado. Enquanto a falha
 * durar, o item mostra um "!" discreto no lugar do número. Na volta, nada: o número reaparece.
 */
type ContadorDoMenu = 'aprovacoes' | 'sla' | 'cilia' | 'rejeicoes' | 'portal' | 'formularios';

const CONTADORES_DO_MENU: Record<ContadorDoMenu, { path: string; nome: string }> = {
  aprovacoes: { path: '/chefe-de-oficina', nome: 'aprovações pendentes do Chefe de Oficina' },
  sla: { path: '/chefe-de-oficina', nome: 'prazos vencendo do Chefe de Oficina' },
  cilia: { path: '/orcamentos-cilia', nome: 'autorizações de Requisições' },
  rejeicoes: { path: '/controle-producao', nome: 'horas reprovadas de Meus Veículos' },
  portal: { path: '/portal-cotacoes', nome: 'avisos do Portal de Cotações' },
  formularios: { path: '/formularios', nome: 'formulários pendentes' },
};

const TITULO_CONTADOR_COM_FALHA = 'Não foi possível atualizar este contador';

const Sidebar: React.FC<SidebarProps> = ({ className, isSidebarOpen, toggleSidebar }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const sidebarRef = useRef<HTMLDivElement>(null);

  const [userId, setUserId] = useState<string | null>(null);
  const [userType, setUserType] = useState<DecodedToken['userType'] | null>(null);
  // at-81: o menu é a UNIÃO dos papéis. `userType` continua existindo para o rótulo do perfil e
  // para os itens que mudam de FORMA conforme o papel (ex: "Meus Veículos" x "Tempo e SLA").
  const [papeis, setPapeis] = useState<string[]>([]);

  const [isAdmin, setIsAdmin] = useState<boolean>(false);

  /**
   * O usuário alcança este path por ALGUM dos seus papéis? (at-81)
   *
   * Usado pelos pollings de badge: sem isto, quem não alcança a tela dispararia 403 a cada 30s
   * (a razão pela qual a checagem existe desde o BUG-V38).
   */
  const alcancaPath = useCallback(
    (path: string): boolean =>
      isAdmin || papeis.some((papel) => (permissionsPorPapel()[papel] ?? []).includes(path)),
    [isAdmin, papeis],
  );

  const [pendingApprovalsCount, setPendingApprovalsCount] = useState<number>(0);
  const [pendingSLACount, setPendingSLACount] = useState<number>(0);
  const [pendingFormsCount, setPendingFormsCount] = useState<number>(0);
  const [myRejectionsCount, setMyRejectionsCount] = useState<number>(0);
  // Complementos do Cilia aguardando autorização do Chefe (29/07/2026)
  const [ciliaAutorizacoesCount, setCiliaAutorizacoesCount] = useState<number>(0);
  // Avisos não lidos do Portal de Cotações (at-06) — o mesmo número do badge do sino.
  const [portalAvisosCount, setPortalAvisosCount] = useState<number>(0);
  const [userProfile, setUserProfile] = useState<User | null>(null);

  // BUG-005: quais contadores estão falhando agora. O ref decide a transição sem depender do
  // estado capturado por cada intervalo.
  const [contadoresComFalha, setContadoresComFalha] = useState<Set<ContadorDoMenu>>(new Set());
  const falhasRef = useRef<Set<ContadorDoMenu>>(new Set());
  const registrarContador = useCallback((contador: ContadorDoMenu, ok: boolean) => {
    const falhava = falhasRef.current.has(contador);
    if (ok !== falhava) return;
    const proximo = new Set(falhasRef.current);
    if (ok) proximo.delete(contador);
    else proximo.add(contador);
    falhasRef.current = proximo;
    setContadoresComFalha(proximo);
    if (!ok) {
      notify.warning(
        `Não foi possível atualizar o contador de ${CONTADORES_DO_MENU[contador].nome} no menu. O item mostra "!" até a próxima atualização dar certo.`,
        { toastId: `sidebar-contador-${contador}` },
      );
    }
  }, []);
  const contar = useCallback(
    <T,>(contador: ContadorDoMenu, consulta: Promise<T>, aplicar: (valor: T) => void) =>
      consulta
        .then((valor) => {
          aplicar(valor);
          registrarContador(contador, true);
        })
        .catch(() => registrarContador(contador, false)),
    [registrarContador],
  );

  /**
   * at-108: notificações não lidas, quebradas pela TELA a que cada aviso se refere.
   *
   * O sino saiu da Sidebar; o que sobra aqui é o número, somado ao badge que o item já tinha —
   * o Chefe de Oficina, por exemplo, mostra aprovações + SLA + avisos não lidos dele.
   */
  const notificacoesPorTela = useNotificacoesPorTela(alcancaPath, Boolean(userType));

  useEffect(() => {
    if (userId) {
      getUserById(userId)
        .then(setUserProfile)
        .catch((err) => {
          console.error('Erro ao carregar perfil do usuário no Sidebar:', err);
          // O menu acompanha toda a navegacao: toastId fixo para o aviso nao empilhar.
          notify.error('Não foi possível carregar seu perfil. Recarregue a página; se persistir, acione a TI.', {
            toastId: 'sidebar-perfil',
          });
        });
    }
  }, [userId]);

  useEffect(() => {
    const handleBodyClass = () => {
      const isMobile = window.innerWidth <= 768;
      if (isMobile && isSidebarOpen) {
        document.body.classList.add('sidebar-open-mobile');
      } else {
        document.body.classList.remove('sidebar-open-mobile');
      }
    };

    handleBodyClass();
    window.addEventListener('resize', handleBodyClass);
    return () => {
      window.removeEventListener('resize', handleBodyClass);
      document.body.classList.remove('sidebar-open-mobile');
    };
  }, [isSidebarOpen]);

  useEffect(() => {
    if (!['CHEFE_DE_OFICINA', 'ADMIN'].includes(userType || '')) return;

    const fetch = () => {
      contar('aprovacoes', getPendingApprovalsCount(), setPendingApprovalsCount);
      contar('sla', getPendingSLACount(), setPendingSLACount);
      // Badge da fila de complementos do Cilia. Só busca com o módulo no ar — sem a env,
      // a chamada iria para `undefined/...`. O `.catch` recebe o mesmo tratamento dos
      // outros contadores (regra da at-60).
      // A checagem da flag (at-74) segue a mesma lógica do item de menu: em teste,
      // só o ADMIN tem o módulo — CHEFE_DE_OFICINA não busca a fila do Cilia.
      // Desde 22/09/2026 o Cilia está liberado, então `moduloEmModoTeste` devolve false e
      // o CHEFE volta a buscar a fila — a decisão vive em `MODULOS_LIBERADOS`, não aqui.
      if (ORCAMENTO_CILIA_HABILITADO && (!moduloEmModoTeste('orcamento-cilia') || userType === 'ADMIN')) {
        contar('cilia', getAutorizacoesPendentesCount(), setCiliaAutorizacoesCount);
      }
    };

    fetch();
    const interval = setInterval(fetch, 30_000);

    /*
     * Push do websocket do Cilia (ADR 0003 do módulo): o badge da fila de complementos sobe
     * na hora, sem esperar o ciclo. O `setInterval` acima **continua** — socket é
     * aceleração, não substituto: com a conexão caída o menu volta a se comportar como
     * antes, em vez de congelar. Mesmo desenho do listener do Portal, logo abaixo.
     */
    const aoReceberAvisoCilia = () => fetch();
    window.addEventListener('cilia:notificacao', aoReceberAvisoCilia);

    return () => {
      clearInterval(interval);
      window.removeEventListener('cilia:notificacao', aoReceberAvisoCilia);
    };
  }, [userType, contar]);

  useEffect(() => {
    if (userType !== 'COLABORADOR') return;

    const fetch = () => {
      contar('rejeicoes', getMyRejections(), (res) => setMyRejectionsCount(res?.length || 0));
    };

    fetch();
    const interval = setInterval(fetch, 30_000);
    return () => clearInterval(interval);
  }, [userType, contar]);

  /**
   * Badge do Portal de Cotações (at-06): avisos não lidos do módulo.
   *
   * Duas condições, e as duas importam:
   *  - **módulo no ar** (`PORTAL_COTACOES_HABILITADO`): sem a env, a chamada iria para
   *    `undefined/notificacoes` — mesmo cuidado do badge do Cilia;
   *  - **papel que vê o item** (lição do at-67/BUG-V38): sem isto, o poll de 30 em 30
   *    segundos rodaria para todo papel logado, inclusive quem nem tem a tela no menu, e
   *    cada ciclo seria uma requisição que só poderia devolver 403.
   *
   * O número é o mesmo do sino da tela; os dois pollings são independentes e convergem no
   * ciclo seguinte, como já acontece com os demais contadores do menu.
   */
  useEffect(() => {
    if (!userType) return;
    if (!PORTAL_COTACOES_HABILITADO) return;
    // O próprio mapa que decide o que este menu renderiza — sem duplicar a regra.
    if (!alcancaPath('/portal-cotacoes')) return;
    // Flag de teste (at-74): sem o item no menu, também não há polling do badge.
    if (modulosNovosSomenteAdmin() && userType !== 'ADMIN') return;

    const fetch = () => contar('portal', contarAvisosNaoLidosPortal(), setPortalAvisosCount);

    fetch();
    const interval = setInterval(fetch, 30_000);

    /*
     * Push do websocket (ADR 0005 do Portal): o badge sobe na hora, sem esperar o ciclo.
     * O `setInterval` acima **continua** — socket é aceleração, não substituto: com a
     * conexão caída, o menu volta a se comportar como antes em vez de congelar.
     */
    const aoReceberAviso = () => fetch();
    window.addEventListener('portal:notificacao', aoReceberAviso);

    return () => {
      clearInterval(interval);
      window.removeEventListener('portal:notificacao', aoReceberAviso);
    };
  }, [alcancaPath, userType, contar]);

  /**
   * at-67 (BUG-V38): o contador de Formulários só é buscado por quem vê o item no menu.
   *
   * O poll de 30 em 30 segundos rodava para **todo papel logado**, inclusive
   * Relacionamento, Contac e Administração — que não têm a tela de Formulários no
   * `RoleGuard` e nunca veriam o badge. Cada ciclo era uma requisição que só podia
   * devolver zero (o backend filtra pelos papéis autorizados no template) ou um 403.
   * A consulta em si também ficou barata na mesma entrega, do lado do backend.
   */
  useEffect(() => {
    if (!userType) return;
    // O próprio mapa que decide o que este menu renderiza — sem duplicar a regra.
    if (!alcancaPath('/formularios')) return;

    const fetch = () => contar('formularios', getPendingFormsCount(), setPendingFormsCount);

    fetch();
    const interval = setInterval(fetch, 30_000);
    return () => clearInterval(interval);
  }, [alcancaPath, userType, contar]);

  useEffect(() => {
    const token = lerTokenDoCookie() || localStorage.getItem('authToken');

    if (token) {
      try {
        const decoded = jwtDecode<DecodedToken>(token);

        setUserId(decoded.sub);
        setUserType(decoded.userType);
        setPapeis(papeisDe(decoded));
        // Administrador é quem TEM o papel ADMIN, em qualquer posição (at-81). A flag isAdmin
        // do JWT segue legada (ADR 0010).
        setIsAdmin(ehAdmin(decoded));
      } catch (error) {
        console.error('Erro ao decodificar o token:', error);
      }
    }
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (isSidebarOpen && sidebarRef.current && !sidebarRef.current.contains(target)) {
        localStorage.setItem('sidebarOpen', 'false');
        toggleSidebar();
      }
    };

    if (isSidebarOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isSidebarOpen, toggleSidebar]);

  const handleNavigate = (path: string) => {
    navigate(path);
  };

  /**
   * at-130: o tema é escolha de cada usuário — claro, escuro ou seguir o sistema —, num menu que
   * abre para fora do menu lateral ao passar o mouse (ou ao clicar, no toque e no teclado). A tela
   * muda na hora; se o servidor recusar, volta ao anterior e avisa — nunca parece salvo o que não foi.
   *
   * ⚠️ O menu é desenhado num portal, em `position: fixed`: a Sidebar tem `overflow: hidden`, e
   * dentro dela o menu seria cortado.
   */
  const temaAtual = useTemaConfigurado();
  const gatilhoTemaRef = useRef<HTMLButtonElement>(null);
  const fecharTemaTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [menuTema, setMenuTema] = useState<{ top: number; left: number } | null>(null);

  const abrirMenuTema = () => {
    if (fecharTemaTimer.current) clearTimeout(fecharTemaTimer.current);
    const r = gatilhoTemaRef.current?.getBoundingClientRect();
    if (r) setMenuTema({ top: r.top + r.height / 2, left: r.right + 8 });
  };
  // Pequena espera ao sair: o mouse atravessa o vão entre o botão e o menu.
  const fecharMenuTemaDepois = () => {
    if (fecharTemaTimer.current) clearTimeout(fecharTemaTimer.current);
    fecharTemaTimer.current = setTimeout(() => setMenuTema(null), 180);
  };

  useEffect(() => {
    if (!menuTema) return undefined;
    const aoTeclar = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMenuTema(null);
    };
    document.addEventListener('keydown', aoTeclar);
    return () => document.removeEventListener('keydown', aoTeclar);
  }, [menuTema]);

  useEffect(() => () => {
    if (fecharTemaTimer.current) clearTimeout(fecharTemaTimer.current);
  }, []);

  const escolherTema = async (tema: TemaConfigurado) => {
    setMenuTema(null);
    const anterior = temaAtual;
    if (tema === anterior) return;
    definirTemaConfigurado(tema);
    try {
      await salvarTemaDaAplicacao(tema);
    } catch (error) {
      definirTemaConfigurado(anterior);
      notify.actionError(error, { action: 'update', subject: 'o tema' });
    }
  };

  /**
   * at-72: o logout precisa AVISAR O SERVIDOR, não só limpar o navegador.
   *
   * Só `POST /auth/logout` apaga o cookie `refreshToken` (httpOnly, 7 dias — fora do alcance
   * do JavaScript), revoga o access token em `revoked_tokens` e apaga os refresh tokens do
   * banco. Antes desta versão nada disso acontecia: a sessão continuava viva no servidor.
   *
   * A limpeza local e a navegação ficam no `finally` de propósito — falha de rede não pode
   * prender o usuário logado numa tela da qual ele pediu para sair.
   */
  const handleLogout = async () => {
    try {
      await logoutUser();
    } catch {
      // Texto fixo, não `errorFrom`: o que importa aqui é a CONSEQUÊNCIA (a sessão pode
      // seguir válida em outro lugar), não o motivo HTTP da falha — regra da at-65.
      notify.warning(
        'Não foi possível encerrar sua sessão no servidor. Você saiu neste dispositivo, mas o acesso pode continuar válido em outro lugar por até 2 horas.',
        { toastId: 'sidebar-logout' },
      );
    } finally {
      localStorage.removeItem('authToken');

      dispatch(clearAccessToken());

      limparCookieDaSessao();
      navigate('/login');
    }
  };

  /**
   * at-81: os itens são a UNIÃO dos paths de todos os papéis do usuário — é o que faz quem
   * acumula funções ver os dois blocos de menu. Sem papel nenhum (token velho e ilegível) o
   * menu fica vazio, em vez de assumir COLABORADOR como antes: oferecer o menu de um papel que
   * a pessoa talvez não tenha produz 403 em todo clique.
   */
  const PATHS_DO_CONTAC = [
    '/contac-dashboard',
    '/contac-dashboard-regional',
    '/contac-dashboard-consultor',
    '/resultado-por-placa',
    '/contac/dashboard-associacao',
    '/contac/radar',
  ];

  const getFilteredItems = () => {
    let itens = isAdmin
      ? [...adminPermissions]
      : Array.from(new Set(papeis.flatMap((papel) => permissionsPorPapel()[papel] ?? [])));
    // Módulo sem a env do backend dele não existe na UI — é o que mantém os dois
    // módulos de Compras invisíveis em produção, onde o build injeta apenas
    // REACT_APP_BACKEND_URL. Ver PORTAL_COTACOES_HABILITADO / ORCAMENTO_CILIA_HABILITADO.
    // previsao-pecas/at-02: a Previsão de Peças deixou de depender de env (lê o backend do SOAC).
    if (!PORTAL_COTACOES_HABILITADO) itens = itens.filter((path) => path !== '/portal-cotacoes');
    if (!ORCAMENTO_CILIA_HABILITADO) itens = itens.filter((path) => path !== '/orcamentos-cilia');
    if (!BANCO_SYNC_HABILITADO) itens = itens.filter((path) => path !== '/sincronizacao-base');
    if (!FIPE_SYNC_HABILITADO) itens = itens.filter((path) => path !== '/sincronizacao-fipe');
    /*
     * Flag de teste (at-74): com ela ligada, o módulo ainda em teste é só do ADMIN — os demais
     * papéis não veem o item, na mesma linha do RoleGuard das rotas.
     *
     * ⚠️ Desde 22/09/2026 isto é POR MÓDULO, não uma lista de paths: o Cilia foi liberado e o
     * Portal/Estoque não. Quem decide é `MODULOS_LIBERADOS` (shared/modulos-novos.ts) — repetir a
     * escolha aqui faria o menu discordar do RoleGuard, que é o pior desfecho possível (item
     * visível que leva a "acione a TI").
     */
    // Contac descontinuado (19/10/2026): as telas dele saem do menu de todos, menos do ADMIN
    // (decisão do Victor, 28/10/2026). As rotas continuam no ar.
    if (!isAdmin) itens = itens.filter((path) => !PATHS_DO_CONTAC.includes(path));
    if (!isAdmin) {
      const moduloDoPath: Record<string, ModuloNovo> = {
        '/portal-cotacoes': 'portal-cotacoes',
        '/orcamentos-cilia': 'orcamento-cilia',
        '/previsao-pecas': 'previsao-pecas',
      };
      itens = itens.filter((path) => {
        const modulo = moduloDoPath[path];
        return !modulo || !moduloEmModoTeste(modulo);
      });
    }
    return itens;
  };

  const sidebarItems: SidebarItem[] = [
    { path: '/home', label: 'Painel geral', icon: PainelGeral },
    { path: '/recepcao', label: 'Recepção', icon: Recepcao },
    { path: '/chefe-de-oficina', label: 'Chefe de Oficina', icon: ChefeDeOficina, badge: pendingApprovalsCount + pendingSLACount },
    { path: '/administracao', label: 'Administração', icon: AdministracaoIcon },
    // Badge = complementos aguardando autorização (aba "Autorizações", 30/07)
    { path: '/orcamentos-cilia', label: 'Requisições', icon: OrcamentoIcon, badge: ciliaAutorizacoesCount },
    // Badge = avisos não lidos do módulo (at-06), o mesmo número do sino da tela.
    { path: '/portal-cotacoes', label: 'Portal de Cotações', icon: PortalCotacoesIcon, badge: portalAvisosCount },
    { path: '/previsao-pecas', label: 'Previsão de Peças', icon: PrevisaoPecasIcon },

    ...(papeis.some((p) => ['CHEFE_DE_OFICINA', 'COLABORADOR', 'ADMIN', 'TI'].includes(p))
      ? [
          {
            path: '/controle-producao',
            label: userType === 'COLABORADOR' ? 'Meus Veículos' : 'Tempo e SLA',
            icon: userType === 'COLABORADOR' ? MeusCarros : ClockIcon,
            badge: userType === 'COLABORADOR' ? myRejectionsCount : undefined,
          },
        ]
      : []),

    { path: '/formularios', label: 'Formulários', icon: Diagrama, badge: pendingFormsCount },
    { path: '/auditoria', label: 'Auditoria', icon: Relatorio },
    { path: '/tela-de-controle', label: 'Tela de Controle', icon: TelaDeControle },
    { path: '/checklists-realizados', label: 'Checklists Realizados', icon: ChecklistHistorico },
    // at-75: consulta do RASTREADOR. Ícone próprio desde a at-83 (pedido do Victor): o
    // reaproveitado da Tela de Controle era um registro/livro, que não diz nada sobre o que
    // esta tela faz — e desde a at-76 ela responde ONDE o veículo está, não só se ele está.
    { path: '/rastreamento', label: 'Veículos na Oficina', icon: RastreamentoIcon },
    // at-85: a única tela do setor de EVENTOS — etapa atual, prazo da etapa e dias na oficina.
    { path: '/andamento-veiculos', label: 'Andamento dos Veículos', icon: AndamentoIcon },
    // at-114 (ADR 0059): as duas telas da Oficina Externa. A primeira é a do INSPETOR (a árvore
    // Região > Oficina > Placa, que funciona sem rede); a segunda é a leitura de quem não vai à
    // oficina — Chefe e setor de Eventos.
    // Os nomes dizem QUEM usa e O QUÊ (decisão do Victor, 25/09/2026): "Oficina Externa" e
    // "Oficinas Externas" se distinguiam só pelo plural, mal, numa lista de menu. Os ícones: um
    // pin com visto e a rota tracejada até ele (o inspetor a caminho); um carro com o pin de
    // localização (o veículo que está em outro lugar).
    { path: '/oficina-externa', label: 'Minhas Visitas', icon: MinhasVisitasIcon },
    { path: '/oficinas-externas', label: 'Veículos em Oficina Externa', icon: VeiculosOficinaExternaIcon },

    // [FUTURE-REFATORACAO: REMOVE_ESTOQUE_ORCAMENTO]
    // Logs de Peças fora do menu por decisão do usuário (10/08/2026): peças são domínio de
    // Orçamento/Estoque/Compras, suspensos até a V2 — dar navegação seria abrir acesso de UI
    // a área suspensa. A tela e o código dela ficam intactos (segue alcançável por URL);
    // volta ao menu junto com a V2, se for a decisão.
    // { path: '/parts/logs', label: 'Logs de Peças', icon: MovimentacaoDePecas },

    { path: '/comissionamentos', label: 'Comissionamentos', icon: HandshakeIcon },
    { path: '/contac-dashboard', label: 'Contac - Dashboard Categoria', icon: ChartPie },
    { path: '/contac-dashboard-regional', label: 'Contac - Dashboard Regional', icon: ContacCategoria },
    ...(userType !== 'REGIONAL' ? [{ path: '/contac-dashboard-consultor', label: 'Contac - Dashboard Consultor', icon: ContacConsultor }] : []),
    // [FUTURE-REFATORACAO: REMOVE_COOPERATIVAS]
    // ...(userType === 'CONTAC' || userType === 'ADMIN' ? [{ path: '/contac-dashboard-cooperativa', label: 'Contac - Dashboard Cooperativa', icon: ContacCategoria }] : []),
    { path: '/resultado-por-placa', label: 'Contac - Resultado por Placa', icon: ContacPlaca },

    // at-86: painel analítico da carteira. Reusa o ícone de gráfico do Contac — o dado é o mesmo
    // (base financeira do Contac), e arte nova para um item só não se justifica.
    {
      path: '/contac/dashboard-associacao',
      label: 'Contac - Dashboard da Associação',
      // Ícone PRÓPRIO: escudo (a proteção veicular) com barras dentro — a carteira em números.
      icon: DashboardAssociacaoIcon,
    },
    // contac/at-11: as recomendações proativas sobre a base do Contac. Fica ao lado do painel da
    // carteira porque é a leitura da mesma base — um mostra os números, o outro aponta o que neles
    // merece atenção. Reusa o ícone do painel da associação: são a mesma família de leitura, e o
    // item já se distingue pelo rótulo.
    {
      path: '/contac/radar',
      label: 'Contac - Radar Financeiro',
      icon: DashboardAssociacaoIcon,
    },
    // at-86: painel da operação da oficina (atendimentos, esteiras, tempos). Reusa o ícone de
    // gráfico, como os demais painéis analíticos.
    // Ícone PRÓPRIO (velocímetro): reusar o de gráfico do Contac deixaria dois itens vizinhos com
    // o mesmo desenho para o ADMIN, que enxerga os dois painéis.
    { path: '/dashboard-oficina', label: 'Dashboard Oficina', icon: DashboardOficinaIcon },
    // at-100: painel do fluxo de compras (requisições, cotações, pedidos). Reusa o ícone do
    // Dashboard Oficina — são painéis irmãos, e arte nova para um item só não se justifica.
    { path: '/dashboard-compras', label: 'Dashboard de Compras', icon: DashboardComprasIcon },
    { path: '/users-table', icon: Perfil, label: 'Usuários' },
    { path: '/admin/sistema', icon: AdminSistemaIcon, label: 'Administração do Sistema' },
    // Painel do serviço que alimenta o Contac (banco-de-dados-atual). Reusa o ícone do
    // Dashboard Regional — é o dado do Contac; arte nova para um item só não se justifica.
    { path: '/sincronizacao-base', label: 'Sincronização da Base', icon: ContacCategoria },
    // Painel do espelho local da tabela FIPE (fipe-atual). Ícone próprio: um veículo sobre
    // linhas de tabela — a tabela FIPE é uma lista de preços por veículo. Antes emprestava o
    // ícone de gráfico do Contac, que não dizia nada sobre o que a tela faz.
    { path: '/sincronizacao-fipe', label: 'Sincronização FIPE', icon: FipeIcon },
  ];

  /**
   * at-108: as notificações não lidas entram no MESMO badge que o item já usava.
   *
   * Somar (em vez de um segundo selo) é o que o Victor pediu: um número por tela. Um item sem
   * badge próprio — "Andamento dos Veículos", por exemplo — passa a ter um quando há aviso, e
   * volta a não ter quando ele é lido.
   */
  const itensComAvisos: SidebarItem[] = sidebarItems.map((item) => {
    const avisos = notificacoesPorTela[item.path] ?? 0;
    if (!avisos) return item;
    return { ...item, badge: (item.badge ?? 0) + avisos };
  });

  const filteredItems = getFilteredItems();
  const itemAtivo = caminhoDoItemAtivo(location.pathname, filteredItems);

  return (
    <CSSTransition
      in={isSidebarOpen}
      timeout={300}
      classNames="sidebar-transition"
    >
      <div ref={sidebarRef} className={`sidebar ${isSidebarOpen ? 'Property1Aberto' : 'Property1Fechado'} ${className || ''}`}>
        <div className="Logo">
          <div className="LogoContent">
            {!isSidebarOpen && (
              <img
                src={ButtonAbrir}
                alt="Button Abrir"
                className="ButtonAbrir"
                onClick={() => {
                  localStorage.setItem('sidebarOpen', 'true');
                  toggleSidebar();
                }}
              />
            )}

            <img
              src={isSidebarOpen ? Logo : LogoFechado}
              alt="Logo"
              className="LogoImage"
            />

            {isSidebarOpen && (
              <img
                src={ButtonFechar}
                alt="Button Fechar"
                className="ButtonFechar"
                onClick={() => {
                  localStorage.setItem('sidebarOpen', 'false');
                  toggleSidebar();
                }}
              />
            )}
          </div>
        </div>

        <div className="Frame">
          {itensComAvisos
            .filter((item) => filteredItems.includes(item.path))
            .map((item) => {
              // Ativo também nas telas de DENTRO do módulo (ex.: /portal-cotacoes/:id). Com dois
              // itens casando, vence o caminho mais longo, que é o mais específico.
              const isActive = item.path === itemAtivo;
              const contadorFalhou = (Object.keys(CONTADORES_DO_MENU) as ContadorDoMenu[]).some(
                (c) => CONTADORES_DO_MENU[c].path === item.path && contadoresComFalha.has(c),
              );
              return (
                <div
                  key={item.path}
                  className={`MenuItem ${isActive ? 'active' : ''}`}
                  onClick={item.action ? item.action : () => handleNavigate(item.path)}
                >
                  <img src={item.icon} alt={item.label} className="MenuItemIcon" />

                  <span className="opcoesMenu">
                    {item.label}
                  </span>

                  {contadorFalhou ? (
                    <span
                      className="sidebar-item-badge sidebar-item-badge--falha"
                      title={TITULO_CONTADOR_COM_FALHA}
                      aria-label={TITULO_CONTADOR_COM_FALHA}
                    >
                      !
                    </span>
                  ) : typeof item.badge === 'number' && item.badge > 0 && (
                    <span className="sidebar-item-badge">
                      {item.badge}
                    </span>
                  )}
                </div>
              );
            })}
        </div>

        {/*
          at-108: o sino NÃO é um botão da Sidebar (decisão do Victor, 18/09/2026). O menu mostra
          só o número, no item da tela a que o aviso se refere; a lista fica DENTRO da tela, como
          no Portal de Cotações — Chefe de Oficina (onde ele sempre esteve) e Andamento dos
          Veículos, que é a tela do setor de Eventos.
        */}

        <div className="SidebarProfile">
          <div className="SidebarProfileAvatar">
            {getInitials(userProfile?.fullName)}
          </div>
          <div className="SidebarProfileInfo">
            <span className="SidebarProfileName" title={userProfile?.fullName}>
              {userProfile?.fullName || 'Carregando...'}
            </span>
            <span className="SidebarProfileRole">
              {/* at-81: quem acumula funções vê os dois perfis aqui — sem isso a pessoa lê um
                  rótulo que não explica metade do menu que ela está enxergando. */}
              {papeis.length > 0
                ? papeis.map((papel) => userTypeLabels[papel] || papel).join(' · ')
                : userTypeLabels[userProfile?.userType || ''] || userProfile?.userType || ''}
            </span>
          </div>
        </div>

        <button
          ref={gatilhoTemaRef}
          type="button"
          className="MenuItem SidebarTema"
          aria-haspopup="menu"
          aria-expanded={menuTema !== null}
          onMouseEnter={abrirMenuTema}
          onMouseLeave={fecharMenuTemaDepois}
          onClick={() => (menuTema ? setMenuTema(null) : abrirMenuTema())}
        >
          <svg className="MenuItemIcon SidebarTemaIcone" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
            <circle cx="12" cy="12" r="8" />
            <path d="M12 4a8 8 0 0 1 0 16Z" fill="currentColor" stroke="none" />
          </svg>
          <span className="opcoesMenu">{ROTULO_TEMA[temaAtual]}</span>
        </button>

        {menuTema &&
          createPortal(
            <div
              className="SidebarTemaMenu"
              role="menu"
              aria-label="Tema"
              style={{ top: menuTema.top, left: menuTema.left }}
              onMouseEnter={abrirMenuTema}
              onMouseLeave={fecharMenuTemaDepois}
            >
              <span className="SidebarTemaMenuTitulo">Tema</span>
              {OPCOES_DE_TEMA.map((o) => (
                <button
                  key={o.valor}
                  type="button"
                  role="menuitemradio"
                  aria-checked={temaAtual === o.valor}
                  className={`SidebarTemaOpcao${temaAtual === o.valor ? ' SidebarTemaOpcao--ativa' : ''}`}
                  onClick={() => escolherTema(o.valor)}
                >
                  <span className="SidebarTemaOpcaoMarca" aria-hidden="true" />
                  {o.rotulo}
                </button>
              ))}
            </div>,
            document.body,
          )}

        <div className="MenuItem SidebarLogout" onClick={handleLogout}>
          <img src={Logout} alt="Sair" className="MenuItemIcon" />
          <span className="opcoesMenu">Logout</span>
        </div>
      </div>
    </CSSTransition>
  );
};

const OPCOES_DE_TEMA: { valor: TemaConfigurado; rotulo: string }[] = [
  { valor: 'CLARO', rotulo: 'Claro' },
  { valor: 'ESCURO', rotulo: 'Escuro' },
  { valor: 'SISTEMA', rotulo: 'Seguir o sistema' },
];
const ROTULO_TEMA: Record<TemaConfigurado, string> = { CLARO: 'Tema claro', ESCURO: 'Tema escuro', SISTEMA: 'Tema do sistema' };

/**
 * transversal/at-90 — "estes papéis alcançam esta tela?", pela MESMA tabela do menu.
 *
 * Exportada para o sino do topo decidir para onde levar um aviso de veículo: com uma segunda
 * tabela, o sino mandaria o usuário para uma tela que o menu não oferece a ele.
 */
export function alcancaPathPelosPapeis(papeis: string[], path: string): boolean {
  return (
    papeis.includes('ADMIN') ||
    papeis.some((papel) => (permissionsPorPapel()[papel] ?? []).includes(path))
  );
}

/** O item do menu da tela atual: igual ao caminho, ou o prefixo mais longo seguido de "/". */
export function caminhoDoItemAtivo(pathname: string, caminhos: string[]): string | null {
  let melhor: string | null = null;
  for (const c of caminhos) {
    if (pathname === c || pathname.startsWith(c + '/')) {
      if (!melhor || c.length > melhor.length) melhor = c;
    }
  }
  return melhor;
}

export default Sidebar;
